// pivotTable. Backend may pre-aggregate into `data.matrix` (rowKey →
// {colKey: value}); fall back to long-form rows.

import { useMemo, useState } from "react";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import type { DataRow } from "../types/pbi";
import "./_table.css";
// AGENT-T: shared TableEx CSS for headers / zebra / hover, plus per-visual
// pivot CSS for indentation / totals / caret.
import "./TableExVisual.css";
import "./PivotTableVisual.css";

// AGENT-T: numeric detection — same heuristic as TableExVisual.
function isNumericCell(v: unknown): boolean {
  return typeof v === "number";
}
function isNumericFromRows(rows: DataRow[], key: string): boolean {
  for (const r of rows) {
    const v = r?.[key];
    if (v === null || v === undefined) continue;
    return isNumericCell(v);
  }
  return false;
}
function isNumericFromMatrix(
  matrix: Record<string, Record<string, string | number | null>>,
  key: string,
): boolean {
  for (const rk of Object.keys(matrix)) {
    const v = matrix[rk]?.[key];
    if (v === null || v === undefined) continue;
    return isNumericCell(v);
  }
  return false;
}

// AGENT-T: pivot data envelope may carry a `totals` row alongside the
// rows / matrix payload. The shared VisualData type doesn't declare it,
// so we read it through a narrowed cast. Deferred TODO: backend doesn't
// emit `data.totals` today; this branch is a no-op until it does.
interface PivotDataExtra {
  totals?: Record<string, string | number | null>;
}

export function PivotTableVisual({ spec, data }: VisualProps) {
  // AGENT-T: visual-only expand/collapse state per row key. Not wired to
  // the cross-filter store — purely local UX. Default-open; clicking the
  // caret toggles the flag (still a no-op for actual data folding today).
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const toggleRow = (rk: string) =>
    setOpenRows((prev) => ({ ...prev, [rk]: !(prev[rk] ?? true) }));

  const totals = (data as unknown as PivotDataExtra).totals ?? null;

  const matrix = data.matrix;
  const useMatrix = !!(matrix && Object.keys(matrix).length > 0);

  // AGENT-T: flat-rows fallback prep — always derived (cheap), even when
  // we end up rendering the matrix branch, so hook order stays stable.
  const rows: DataRow[] = data.rows ?? [];
  const cols = useMemo<string[]>(
    () => data.columns ?? (rows[0] ? Object.keys(rows[0]) : []),
    [data.columns, rows],
  );

  // AGENT-T: numeric column map — covers both matrix + flat-rows shapes.
  const numericByKey = useMemo<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    if (useMatrix && matrix) {
      const colKeySet = new Set<string>();
      for (const r of Object.keys(matrix))
        for (const c of Object.keys(matrix[r] ?? {})) colKeySet.add(c);
      for (const c of colKeySet) out[c] = isNumericFromMatrix(matrix, c);
    } else {
      for (const k of cols) out[k] = isNumericFromRows(rows, k);
    }
    return out;
  }, [useMatrix, matrix, cols, rows]);

  // ── Matrix branch ────────────────────────────────────────────────────
  if (useMatrix && matrix) {
    const rowKeys = Object.keys(matrix);
    const colKeySet = new Set<string>();
    for (const r of rowKeys)
      for (const c of Object.keys(matrix[r] ?? {})) colKeySet.add(c);
    const colKeys = Array.from(colKeySet);
    const isEmpty = rowKeys.length === 0;

    return (
      <ChartShell title={spec.title} isEmpty={isEmpty} status={data.status}>
        <div className="tableex">
          {isEmpty ? (
            // AGENT-T: centered empty-state placeholder.
            <div className="tableex-empty">No data</div>
          ) : (
            <div className="tableex-scroll">
              <table className="tableex-table">
                <thead>
                  <tr>
                    {/* AGENT-T: leading row-header slot stays sticky-left. */}
                    <th className="tableex-th first" />
                    {colKeys.map((c) => {
                      const cls = [
                        "tableex-th",
                        numericByKey[c] ? "numeric" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <th key={c} className={cls}>
                          {c}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rowKeys.map((rk) => {
                    const open = openRows[rk] ?? true;
                    return (
                      <tr key={rk} className="tableex-tr">
                        {/* AGENT-T: row header with caret + sticky-left. */}
                        <td className="tableex-td first pivot-row-header">
                          <span
                            className="pivot-caret"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(rk);
                            }}
                            role="button"
                            aria-label={open ? "Collapse" : "Expand"}
                          >
                            {open ? "▾" : "▸"}
                          </span>
                          {rk}
                        </td>
                        {colKeys.map((c) => {
                          const v = matrix[rk]?.[c];
                          const cls = [
                            "tableex-td",
                            numericByKey[c] ? "numeric" : "",
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <td key={c} className={cls}>
                              {v === null || v === undefined ? "" : String(v)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {/* AGENT-T: totals row rendered bold w/ top-border when
                      data.totals is present. Backend doesn't emit this
                      field today — deferred TODO. */}
                  {totals ? (
                    <tr className="tableex-tr pivot-totals-row">
                      <td className="tableex-td first pivot-row-header">
                        Total
                      </td>
                      {colKeys.map((c) => {
                        const v = totals[c];
                        const cls = [
                          "tableex-td",
                          numericByKey[c] ? "numeric" : "",
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <td key={c} className={cls}>
                            {v === null || v === undefined ? "" : String(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </ChartShell>
    );
  }

  // ── Flat-rows fallback ───────────────────────────────────────────────
  const isEmpty = rows.length === 0;
  return (
    <ChartShell title={spec.title} isEmpty={isEmpty} status={data.status}>
      <div className="tableex">
        {isEmpty ? (
          // AGENT-T: centered empty-state placeholder.
          <div className="tableex-empty">No data</div>
        ) : (
          <div className="tableex-scroll">
            <table className="tableex-table">
              <thead>
                <tr>
                  {cols.map((c, idx) => {
                    const cls = [
                      "tableex-th",
                      idx === 0 ? "first" : "",
                      numericByKey[c] ? "numeric" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <th key={c} className={cls}>
                        {c}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="tableex-tr">
                    {cols.map((c, idx) => {
                      const v = r[c];
                      const cls = [
                        "tableex-td",
                        idx === 0 ? "first pivot-row-header" : "",
                        numericByKey[c] ? "numeric" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <td key={c} className={cls}>
                          {v === null || v === undefined ? "" : String(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* AGENT-T: totals row mirrors the matrix branch. */}
                {totals ? (
                  <tr className="tableex-tr pivot-totals-row">
                    {cols.map((c, idx) => {
                      const v = totals[c];
                      const cls = [
                        "tableex-td",
                        idx === 0 ? "first pivot-row-header" : "",
                        numericByKey[c] ? "numeric" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      const isFirst = idx === 0;
                      const empty = v === null || v === undefined;
                      return (
                        <td key={c} className={cls}>
                          {isFirst && empty
                            ? "Total"
                            : empty
                            ? ""
                            : String(v)}
                        </td>
                      );
                    })}
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ChartShell>
  );
}
